import React, { useState, useCallback, useEffect } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { RaiseIssueModal } from './components/RaiseIssueModal';
import { toastService } from './services/toast.service';

// Stub interface for issue (replace with your own)
interface IIssue {
  _id: string;
  id?: string;
  issueNumber?: string;
  title?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  component?: any;
  formTemplateId?: string;
}
import './ThresholdAlert.scss';

interface IThresholdAlertProps {
  component: any;
  condition: any;
  currentValue: string | number;
  thresholdValue: string | number;
  formTemplateId?: string;
  onDismiss?: () => void;
  onIssueRaised?: (conditionId: string) => void;
  isIssueRaised?: boolean; // Track if issue has been raised for this condition
}

export const ThresholdAlert: React.FC<IThresholdAlertProps> = ({
  component,
  condition,
  currentValue,
  thresholdValue,
  formTemplateId,
  onIssueRaised,
  isIssueRaised = false
}) => {
  const [showRaiseIssueModal, setShowRaiseIssueModal] = useState(false);
  const [issue, setIssue] = useState<IIssue | null>(null);
  const [isLoadingIssue, setIsLoadingIssue] = useState(false);
  const [localIssueRaised, setLocalIssueRaised] = useState(false);

  // Sync local state with prop - if parent says issue is raised, update local state
  useEffect(() => {
    if (isIssueRaised) {
      setLocalIssueRaised(true);
    }
  }, [isIssueRaised]);

  // Reset local state when condition changes (new condition = new alert)
  useEffect(() => {
    setLocalIssueRaised(false);
  }, [condition?.id]);

  const getOperatorSymbol = (operator: string): string => {
    const symbols: Record<string, string> = {
      'greaterThan': '>',
      'lessThan': '<',
      'greaterThanOrEqual': '≥',
      'lessThanOrEqual': '≤',
      'equals': '=',
      'notEquals': '≠'
    };
    return symbols[operator] || operator;
  };

  const getConditionMessage = (): string => {
    const operatorSymbol = getOperatorSymbol(condition.operator);
    return `${component.basic?.label || 'Value'}: Value ${currentValue} exceeds threshold ${operatorSymbol} ${thresholdValue}`;
  };

  const handleRaiseIssueClick = useCallback(async () => {
    if (isIssueRaised || localIssueRaised) {
      // If we already have the issue stored (from recent creation), use it
      if (issue) {
        setShowRaiseIssueModal(true);
        return;
      }
      
      // Otherwise, fetch the issue data before opening modal
      setIsLoadingIssue(true);
      try {
        // Stub implementation - in a real app, this would fetch from your API
        // For now, we'll just open the modal without fetching
        // You can replace this with your own issue fetching logic
        setIssue(null);
        setShowRaiseIssueModal(true);
      } catch (error: any) {
        console.error('Error fetching issue:', error);
        toastService.showError('Failed to load issue details');
      } finally {
        setIsLoadingIssue(false);
      }
    } else {
      // For new issues, just open the modal
      setIssue(null);
      setShowRaiseIssueModal(true);
    }
  }, [isIssueRaised, localIssueRaised, formTemplateId, component, issue]);

  const handleRaiseIssueClose = useCallback(() => {
    setShowRaiseIssueModal(false);
    // Don't clear issue if it was just created - keep it for viewing
    // Only clear if we're not in "issue raised" state
    if (!isIssueRaised && !localIssueRaised) {
      setIssue(null);
    }
  }, [isIssueRaised, localIssueRaised]);

  const handleRaiseIssueSuccess = useCallback((createdIssue?: IIssue) => {
    setShowRaiseIssueModal(false);
    // Store the created issue if provided, so we can show it immediately when viewing
    if (createdIssue) {
      setIssue(createdIssue);
    } else {
      setIssue(null); // Clear issue state so next view will fetch the new issue
    }
    // Immediately set local state to hide the alert
    setLocalIssueRaised(true);
    // Notify parent that issue has been raised for this condition
    // Call immediately to ensure state updates
    if (onIssueRaised && condition?.id) {
      onIssueRaised(condition.id);
    }
  }, [onIssueRaised, condition]);

  // If issue is raised (either from prop or local state), show a compact indicator instead of the full alert
  if (isIssueRaised || localIssueRaised) {
    return (
      <>
        <div className="threshold-issue-indicator">
          <div className="threshold-issue-indicator-content">
            <CheckCircle2 className="threshold-issue-indicator-icon" size={16} />
            <span className="threshold-issue-indicator-text">Issue raised for this threshold condition</span>
            <button
              type="button"
              onClick={handleRaiseIssueClick}
              className="threshold-issue-indicator-view-btn"
              disabled={isLoadingIssue}
            >
              {isLoadingIssue ? 'Loading...' : 'View Issue'}
            </button>
          </div>
        </div>

        {showRaiseIssueModal && (
          <RaiseIssueModal
            isOpen={showRaiseIssueModal}
            onClose={handleRaiseIssueClose}
            onSuccess={handleRaiseIssueSuccess}
            component={component}
            formTemplateId={formTemplateId}
            notes=""
            attachments={null}
            issue={issue}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="threshold-alert threshold-alert-unresolved">
        <div className="threshold-alert-border" />
        <div className="threshold-alert-content">
          <div className="threshold-alert-header">
            <div className="threshold-alert-title-group">
              <AlertTriangle className="threshold-alert-icon" size={20} />
              <span className="threshold-alert-title">
                Threshold Condition Met - Action Required
              </span>
            </div>
          </div>
          
          <div className="threshold-alert-message">
            {getConditionMessage()}
            <span className="threshold-alert-warning"> You must raise an issue before submitting the form.</span>
          </div>

          <div className="threshold-alert-buttons">
            <button
              type="button"
              onClick={handleRaiseIssueClick}
              className="threshold-alert-raise-issue-btn"
            >
              Raise Issue Now
            </button>
          </div>
        </div>
      </div>

      {showRaiseIssueModal && (
        <RaiseIssueModal
          isOpen={showRaiseIssueModal}
          onClose={handleRaiseIssueClose}
          onSuccess={handleRaiseIssueSuccess}
          component={component}
          formTemplateId={formTemplateId}
          notes=""
          attachments={null}
          issue={issue}
        />
      )}
    </>
  );
};

